import { blank as api } from './blank'
export const addTagTypes = [
  'health',
  'chat-messages',
  'lines',
  'conditions',
  'demo',
  'condition-synonyms',
  'note-style',
  'condition-results',
  'notes',
  'condition-interaction',
  'evidence-interaction',
  'generated-notes',
  'note-generations',
  'group-preferences',
  'default-note-sections',
  'section-types',
  'user-general-preferences',
  'user-product-state',
  'user-prompt-preferences',
  'patient-load',
  'pre-onboarding',
  'candidate-notes',
  'learned-notes',
  'chart-notes',
  'cache',
] as const
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      healthcheck: build.query<HealthcheckApiResponse, HealthcheckApiArg>({
        query: () => ({ url: `/healthcheck` }),
        providesTags: ['health'],
      }),
      upsertChatMessages: build.mutation<
        UpsertChatMessagesApiResponse,
        UpsertChatMessagesApiArg
      >({
        query: (queryArg) => ({
          url: `/chat-messages`,
          method: 'PUT',
          body: queryArg.chatMessageBatch,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['chat-messages'],
      }),
      getLineStatuses: build.mutation<
        GetLineStatusesApiResponse,
        GetLineStatusesApiArg
      >({
        query: (queryArg) => ({
          url: `/regard-lines/status/${queryArg.lineStatus}`,
          method: 'PUT',
          body: queryArg.maxRequiredContent,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
          params: {
            patientId: queryArg.patientId,
            encounterId: queryArg.encounterId,
            baseNoteId: queryArg.baseNoteId,
            conditionIds: queryArg.conditionIds,
          },
        }),
        invalidatesTags: ['lines'],
      }),
      updateLineStatus: build.mutation<
        UpdateLineStatusApiResponse,
        UpdateLineStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/regard-lines/status`,
          method: 'POST',
          body: queryArg.lineStatusUpdateRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['lines'],
      }),
      saveConditionDismissals: build.mutation<
        SaveConditionDismissalsApiResponse,
        SaveConditionDismissalsApiArg
      >({
        query: (queryArg) => ({
          url: `/conditions/dismissals`,
          method: 'POST',
          body: queryArg.saveConditionDismissalsRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['conditions'],
      }),
      getConditionDismissals: build.query<
        GetConditionDismissalsApiResponse,
        GetConditionDismissalsApiArg
      >({
        query: (queryArg) => ({
          url: `/conditions/dismissals`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            patientId: queryArg.patientId,
            encounterId: queryArg.encounterId,
          },
        }),
        providesTags: ['conditions'],
      }),
      resetDemoDatabase: build.mutation<
        ResetDemoDatabaseApiResponse,
        ResetDemoDatabaseApiArg
      >({
        query: (queryArg) => ({
          url: `/demo/reset`,
          method: 'POST',
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['demo'],
      }),
      resetDemoDatabaseForService: build.mutation<
        ResetDemoDatabaseForServiceApiResponse,
        ResetDemoDatabaseForServiceApiArg
      >({
        query: () => ({ url: `/demo/service-reset`, method: 'POST' }),
        invalidatesTags: ['demo'],
      }),
      getConditionStatuses: build.query<
        GetConditionStatusesApiResponse,
        GetConditionStatusesApiArg
      >({
        query: (queryArg) => ({
          url: `/conditions/status`,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
          params: {
            patientId: queryArg.patientId,
            baseNoteId: queryArg.baseNoteId,
            encounterId: queryArg.encounterId,
            statuses: queryArg.statuses,
          },
        }),
        providesTags: ['conditions'],
      }),
      updateConditionStatus: build.mutation<
        UpdateConditionStatusApiResponse,
        UpdateConditionStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/conditions/status`,
          method: 'POST',
          body: queryArg.conditionStatusUpdateRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['conditions'],
      }),
      getConditionSynonyms: build.query<
        GetConditionSynonymsApiResponse,
        GetConditionSynonymsApiArg
      >({
        query: (queryArg) => ({
          url: `/condition-synonyms/`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['condition-synonyms'],
      }),
      getNoteStyle: build.query<GetNoteStyleApiResponse, GetNoteStyleApiArg>({
        query: (queryArg) => ({
          url: `/note-style`,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
        }),
        providesTags: ['note-style'],
      }),
      updateNoteStyle: build.mutation<
        UpdateNoteStyleApiResponse,
        UpdateNoteStyleApiArg
      >({
        query: (queryArg) => ({
          url: `/note-style`,
          method: 'POST',
          body: queryArg.noteStyleUpdateRequest,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
        }),
        invalidatesTags: ['note-style'],
      }),
      getDiagnosedAt: build.query<
        GetDiagnosedAtApiResponse,
        GetDiagnosedAtApiArg
      >({
        query: (queryArg) => ({
          url: `/condition-results/diagnosed-at/`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            patientId: queryArg.patientId,
            timestamp: queryArg.timestamp,
          },
        }),
        providesTags: ['condition-results'],
      }),
      getConditionResults: build.query<
        GetConditionResultsApiResponse,
        GetConditionResultsApiArg
      >({
        query: (queryArg) => ({
          url: `/condition-results/`,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
          params: {
            patientId: queryArg.patientId,
            encounterId: queryArg.encounterId,
            baseNoteId: queryArg.baseNoteId,
            review: queryArg.review,
            timestamp: queryArg.timestamp,
          },
        }),
        providesTags: ['condition-results'],
      }),
      getCyobNoteOptions: build.query<
        GetCyobNoteOptionsApiResponse,
        GetCyobNoteOptionsApiArg
      >({
        query: (queryArg) => ({
          url: `/base-notes/${queryArg.patientId}`,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
          params: {
            encounterId: queryArg.encounterId,
          },
        }),
        providesTags: ['notes'],
      }),
      recordConditionInteraction: build.mutation<
        RecordConditionInteractionApiResponse,
        RecordConditionInteractionApiArg
      >({
        query: (queryArg) => ({
          url: `/condition-interaction/`,
          method: 'POST',
          body: queryArg.conditionInteractionRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['condition-interaction'],
      }),
      recordEvidenceInteraction: build.mutation<
        RecordEvidenceInteractionApiResponse,
        RecordEvidenceInteractionApiArg
      >({
        query: (queryArg) => ({
          url: `/evidence-interaction/`,
          method: 'POST',
          body: queryArg.evidenceInteractionRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['evidence-interaction'],
      }),
      createGeneratedNotePrototype: build.mutation<
        CreateGeneratedNotePrototypeApiResponse,
        CreateGeneratedNotePrototypeApiArg
      >({
        query: (queryArg) => ({
          url: `/max-generated-notes`,
          method: 'POST',
          body: queryArg.createGeneratedNotePrototypeRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['generated-notes'],
      }),
      getGeneratedNotePrototype: build.query<
        GetGeneratedNotePrototypeApiResponse,
        GetGeneratedNotePrototypeApiArg
      >({
        query: (queryArg) => ({
          url: `/max-generated-notes`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            patient_id: queryArg.patientId,
            encounter_id: queryArg.encounterId,
            note_type: queryArg.noteType,
            prompt_id: queryArg.promptId,
          },
        }),
        providesTags: ['generated-notes'],
      }),
      updateGeneratedNotePrototype: build.mutation<
        UpdateGeneratedNotePrototypeApiResponse,
        UpdateGeneratedNotePrototypeApiArg
      >({
        query: (queryArg) => ({
          url: `/max-generated-notes/${queryArg.noteId}`,
          method: 'PUT',
          body: queryArg.updateMaxGeneratedNotePrototypeRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['generated-notes'],
      }),
      recordNoteGeneration: build.mutation<
        RecordNoteGenerationApiResponse,
        RecordNoteGenerationApiArg
      >({
        query: (queryArg) => ({
          url: `/note-generations`,
          method: 'POST',
          body: queryArg.noteGeneration,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
        }),
        invalidatesTags: ['note-generations'],
      }),
      saveGeneratedNote: build.mutation<
        SaveGeneratedNoteApiResponse,
        SaveGeneratedNoteApiArg
      >({
        query: (queryArg) => ({
          url: `/generated-notes/`,
          method: 'POST',
          body: queryArg.saveGeneratedNoteRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['generated-notes'],
      }),
      v2CreateGeneratedNotePrototype: build.mutation<
        V2CreateGeneratedNotePrototypeApiResponse,
        V2CreateGeneratedNotePrototypeApiArg
      >({
        query: (queryArg) => ({
          url: `/generated-notes/prototype`,
          method: 'POST',
          body: queryArg.createGeneratedNotePrototypeRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['generated-notes'],
      }),
      v2GetGeneratedNotePrototype: build.query<
        V2GetGeneratedNotePrototypeApiResponse,
        V2GetGeneratedNotePrototypeApiArg
      >({
        query: (queryArg) => ({
          url: `/generated-notes/prototype`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            patient_id: queryArg.patientId,
            encounter_id: queryArg.encounterId,
            note_type: queryArg.noteType,
          },
        }),
        providesTags: ['generated-notes'],
      }),
      v2UpdateGeneratedNotePrototype: build.mutation<
        V2UpdateGeneratedNotePrototypeApiResponse,
        V2UpdateGeneratedNotePrototypeApiArg
      >({
        query: (queryArg) => ({
          url: `/generated-notes/prototype/${queryArg.noteId}`,
          method: 'PUT',
          body: queryArg.updateGeneratedNotePrototypeRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['generated-notes'],
      }),
      getGeneratedNote: build.query<
        GetGeneratedNoteApiResponse,
        GetGeneratedNoteApiArg
      >({
        query: (queryArg) => ({
          url: `/generated-notes/${queryArg.baseNoteId}`,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
          params: {
            patient_id: queryArg.patientId,
            encounter_id: queryArg.encounterId,
            force_refresh: queryArg.forceRefresh,
            rebase_note: queryArg.rebaseNote,
          },
        }),
        providesTags: ['notes'],
      }),
      getGroupPreferences: build.query<
        GetGroupPreferencesApiResponse,
        GetGroupPreferencesApiArg
      >({
        query: (queryArg) => ({
          url: `/group-preferences`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['group-preferences'],
      }),
      upsertGroupPreferences: build.mutation<
        UpsertGroupPreferencesApiResponse,
        UpsertGroupPreferencesApiArg
      >({
        query: (queryArg) => ({
          url: `/group-preferences`,
          method: 'PUT',
          body: queryArg.groupPreferencesUpdateRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['group-preferences'],
      }),
      getGroupPreferencesAsRegardAdmin: build.query<
        GetGroupPreferencesAsRegardAdminApiResponse,
        GetGroupPreferencesAsRegardAdminApiArg
      >({
        query: (queryArg) => ({
          url: `/group-preferences/admin/${queryArg.groupId}`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['group-preferences'],
      }),
      upsertGroupPreferencesAsRegardAdmin: build.mutation<
        UpsertGroupPreferencesAsRegardAdminApiResponse,
        UpsertGroupPreferencesAsRegardAdminApiArg
      >({
        query: (queryArg) => ({
          url: `/group-preferences/admin/${queryArg.groupId}`,
          method: 'PUT',
          body: queryArg.groupPreferencesUpdateRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['group-preferences'],
      }),
      getDefaultNoteSections: build.query<
        GetDefaultNoteSectionsApiResponse,
        GetDefaultNoteSectionsApiArg
      >({
        query: (queryArg) => ({
          url: `/default-note-sections`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            noteType: queryArg.noteType,
          },
        }),
        providesTags: ['default-note-sections'],
      }),
      getSectionTypes: build.query<
        GetSectionTypesApiResponse,
        GetSectionTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/section-types`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['section-types'],
      }),
      getUserGeneralPreferences: build.query<
        GetUserGeneralPreferencesApiResponse,
        GetUserGeneralPreferencesApiArg
      >({
        query: (queryArg) => ({
          url: `/user-general-preferences`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['user-general-preferences'],
      }),
      upsertUserGeneralPreferences: build.mutation<
        UpsertUserGeneralPreferencesApiResponse,
        UpsertUserGeneralPreferencesApiArg
      >({
        query: (queryArg) => ({
          url: `/user-general-preferences`,
          method: 'PUT',
          body: queryArg.userGeneralPreferencesUpdateRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['user-general-preferences'],
      }),
      getUserProductState: build.query<
        GetUserProductStateApiResponse,
        GetUserProductStateApiArg
      >({
        query: (queryArg) => ({
          url: `/user-product-state`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['user-product-state'],
      }),
      upsertUserProductState: build.mutation<
        UpsertUserProductStateApiResponse,
        UpsertUserProductStateApiArg
      >({
        query: (queryArg) => ({
          url: `/user-product-state`,
          method: 'PUT',
          body: queryArg.userProductStateUpdateRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['user-product-state'],
      }),
      getUserProductStatesBulkAsRegardAdmin: build.mutation<
        GetUserProductStatesBulkAsRegardAdminApiResponse,
        GetUserProductStatesBulkAsRegardAdminApiArg
      >({
        query: (queryArg) => ({
          url: `/user-product-state/admin/bulk`,
          method: 'POST',
          body: queryArg.bulkUserProductStateRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['user-product-state'],
      }),
      resetUserOnboardingAsRegardAdmin: build.mutation<
        ResetUserOnboardingAsRegardAdminApiResponse,
        ResetUserOnboardingAsRegardAdminApiArg
      >({
        query: (queryArg) => ({
          url: `/user-product-state/admin/${queryArg.userId}/reset`,
          method: 'PUT',
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['user-product-state'],
      }),
      getUserPromptPreferences: build.query<
        GetUserPromptPreferencesApiResponse,
        GetUserPromptPreferencesApiArg
      >({
        query: (queryArg) => ({
          url: `/user-prompt-preferences`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['user-prompt-preferences'],
      }),
      upsertUserPromptPreferences: build.mutation<
        UpsertUserPromptPreferencesApiResponse,
        UpsertUserPromptPreferencesApiArg
      >({
        query: (queryArg) => ({
          url: `/user-prompt-preferences`,
          method: 'PUT',
          body: queryArg.userPromptPreferencesUpdateRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['user-prompt-preferences'],
      }),
      getAllUserBasePrompts: build.query<
        GetAllUserBasePromptsApiResponse,
        GetAllUserBasePromptsApiArg
      >({
        query: (queryArg) => ({
          url: `/user-prompt-preferences/all-user-base-prompts`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['user-prompt-preferences'],
      }),
      triggerPatientLoad: build.mutation<
        TriggerPatientLoadApiResponse,
        TriggerPatientLoadApiArg
      >({
        query: (queryArg) => ({
          url: `/patient-load/`,
          method: 'POST',
          body: queryArg.patientLoadRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['patient-load'],
      }),
      getPreOnboardingPreview: build.query<
        GetPreOnboardingPreviewApiResponse,
        GetPreOnboardingPreviewApiArg
      >({
        query: (queryArg) => ({
          url: `/pre-onboarding/preview`,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
          params: {
            user_id: queryArg.userId,
          },
        }),
        providesTags: ['pre-onboarding'],
      }),
      triggerPreOnboarding: build.mutation<
        TriggerPreOnboardingApiResponse,
        TriggerPreOnboardingApiArg
      >({
        query: (queryArg) => ({
          url: `/pre-onboarding`,
          method: 'POST',
          body: queryArg.triggerPreOnboardingRequest,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
        }),
        invalidatesTags: ['pre-onboarding'],
      }),
      getPreOnboardingStatus: build.query<
        GetPreOnboardingStatusApiResponse,
        GetPreOnboardingStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/pre-onboarding/status`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            user_id: queryArg.userId,
          },
        }),
        providesTags: ['pre-onboarding'],
      }),
      getUserPreOnboardingStatus: build.query<
        GetUserPreOnboardingStatusApiResponse,
        GetUserPreOnboardingStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/pre-onboarding/user-status`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['pre-onboarding'],
      }),
      getPreOnboardingStatusReport: build.mutation<
        GetPreOnboardingStatusReportApiResponse,
        GetPreOnboardingStatusReportApiArg
      >({
        query: (queryArg) => ({
          url: `/pre-onboarding/status-report`,
          method: 'POST',
          body: queryArg.statusReportRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['pre-onboarding'],
      }),
      verifyPreOnboarding: build.mutation<
        VerifyPreOnboardingApiResponse,
        VerifyPreOnboardingApiArg
      >({
        query: (queryArg) => ({
          url: `/pre-onboarding/verify`,
          method: 'POST',
          body: queryArg.verifyPreOnboardingRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['pre-onboarding'],
      }),
      getPreOnboardingBatchProgress: build.query<
        GetPreOnboardingBatchProgressApiResponse,
        GetPreOnboardingBatchProgressApiArg
      >({
        query: (queryArg) => ({
          url: `/pre-onboarding/batch/${queryArg.batchId}`,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        providesTags: ['pre-onboarding'],
      }),
      getPreOnboardingCandidatesReport: build.mutation<
        GetPreOnboardingCandidatesReportApiResponse,
        GetPreOnboardingCandidatesReportApiArg
      >({
        query: (queryArg) => ({
          url: `/pre-onboarding/candidates-report`,
          method: 'POST',
          body: queryArg.candidatesReportRequest,
          cookies: {
            ht_session: queryArg.htSession,
          },
        }),
        invalidatesTags: ['pre-onboarding'],
      }),
      getCandidateNotes: build.query<
        GetCandidateNotesApiResponse,
        GetCandidateNotesApiArg
      >({
        query: (queryArg) => ({
          url: `/candidate-notes`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            note_type: queryArg.noteType,
            limit: queryArg.limit,
          },
        }),
        providesTags: ['candidate-notes'],
      }),
      getLearnedNotesForNoteType: build.query<
        GetLearnedNotesForNoteTypeApiResponse,
        GetLearnedNotesForNoteTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/learned-notes`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            note_type: queryArg.noteType,
          },
        }),
        providesTags: ['learned-notes'],
      }),
      learnFromNoteForNoteType: build.mutation<
        LearnFromNoteForNoteTypeApiResponse,
        LearnFromNoteForNoteTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/learned-notes`,
          method: 'POST',
          body: queryArg.learnFromNoteRequest,
          cookies: {
            ht_session: queryArg.htSession,
            automed_oauth_subject: queryArg.automedOauthSubject,
          },
        }),
        invalidatesTags: ['learned-notes'],
      }),
      getChartNotes: build.query<GetChartNotesApiResponse, GetChartNotesApiArg>(
        {
          query: (queryArg) => ({
            url: `/chart-notes`,
            cookies: {
              ht_session: queryArg.htSession,
            },
            params: {
              note_type: queryArg.noteType,
              limit: queryArg.limit,
            },
          }),
          providesTags: ['chart-notes'],
        },
      ),
      getChartNoteContent: build.query<
        GetChartNoteContentApiResponse,
        GetChartNoteContentApiArg
      >({
        query: (queryArg) => ({
          url: `/chart-notes/content`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            note_type: queryArg.noteType,
            source_id: queryArg.sourceId,
          },
        }),
        providesTags: ['chart-notes'],
      }),
      refreshCache: build.query<RefreshCacheApiResponse, RefreshCacheApiArg>({
        query: (queryArg) => ({
          url: `/refresh/`,
          cookies: {
            ht_session: queryArg.htSession,
          },
          params: {
            patient_id: queryArg.patientId,
          },
        }),
        providesTags: ['cache'],
      }),
    }),
    overrideExisting: false,
  })
export { injectedRtkApi as editorApi }
export type HealthcheckApiResponse =
  /** status 200 Health status information */ HealthCheckResponse
export type HealthcheckApiArg = void
export type UpsertChatMessagesApiResponse = unknown
export type UpsertChatMessagesApiArg = {
  htSession?: string | null
  chatMessageBatch: ChatMessageBatch
}
export type GetLineStatusesApiResponse =
  /** status 200 Line status information for the patient and encounter */ NoteLineStatusResponse
export type GetLineStatusesApiArg = {
  lineStatus: string
  patientId: string
  encounterId: string
  baseNoteId: string
  conditionIds?: string[] | null
  htSession?: string | null
  automedOauthSubject?: string | null
  maxRequiredContent: MaxRequiredContent | null
}
export type UpdateLineStatusApiResponse =
  /** status 200 Confirmation of the status update */ LineStatusUpdateResponse
export type UpdateLineStatusApiArg = {
  htSession?: string | null
  lineStatusUpdateRequest: LineStatusUpdateRequest
}
export type SaveConditionDismissalsApiResponse =
  /** status 200 Confirmation of the upsert */ SaveConditionDismissalsResponse
export type SaveConditionDismissalsApiArg = {
  htSession?: string | null
  saveConditionDismissalsRequest: SaveConditionDismissalsRequest
}
export type GetConditionDismissalsApiResponse =
  /** status 200 List of active dismissals */ GetConditionDismissalsResponse
export type GetConditionDismissalsApiArg = {
  patientId: string
  encounterId: string
  htSession?: string | null
}
export type ResetDemoDatabaseApiResponse =
  /** status 200 Confirmation that the reset completed */ DemoResetResponse
export type ResetDemoDatabaseApiArg = {
  htSession?: string | null
}
export type ResetDemoDatabaseForServiceApiResponse =
  /** status 200 Confirmation that the reset completed */ DemoResetResponse
export type ResetDemoDatabaseForServiceApiArg = void
export type GetConditionStatusesApiResponse =
  /** status 200 Condition status information for the encounter */ ConditionStatusResponse
export type GetConditionStatusesApiArg = {
  patientId: string
  baseNoteId: string
  encounterId: string
  statuses?: ConditionCategory[] | null
  htSession?: string | null
  automedOauthSubject?: string | null
}
export type UpdateConditionStatusApiResponse =
  /** status 200 Confirmation of the status update */ ConditionStatusUpdateResponse
export type UpdateConditionStatusApiArg = {
  htSession?: string | null
  conditionStatusUpdateRequest: ConditionStatusUpdateRequest
}
export type GetConditionSynonymsApiResponse =
  /** status 200 Condition synonyms from Nexus and legacy condition synonyms */ ConditionSynonyms
export type GetConditionSynonymsApiArg = {
  htSession?: string | null
}
export type GetNoteStyleApiResponse =
  /** status 200 Note style for the user */ NoteStyleResponse
export type GetNoteStyleApiArg = {
  htSession?: string | null
  automedOauthSubject?: string | null
}
export type UpdateNoteStyleApiResponse =
  /** status 200 Confirmation of the note style update */ NoteStyleUpdateResponse
export type UpdateNoteStyleApiArg = {
  htSession?: string | null
  automedOauthSubject?: string | null
  noteStyleUpdateRequest: NoteStyleUpdateRequest
}
export type GetDiagnosedAtApiResponse =
  /** status 200 Successful Response */ DiagnosedAtResponse
export type GetDiagnosedAtApiArg = {
  patientId: string
  timestamp?: string | null
  htSession?: string | null
}
export type GetConditionResultsApiResponse =
  /** status 200 Condition results for the patient */ ConditionResultsResponse
export type GetConditionResultsApiArg = {
  patientId: string
  encounterId: string
  baseNoteId: string
  review?: boolean
  timestamp?: string | null
  htSession?: string | null
  automedOauthSubject?: string | null
}
export type GetCyobNoteOptionsApiResponse =
  /** status 200 Base note options with preview for the patient */ CyobNoteOptionsResponse
export type GetCyobNoteOptionsApiArg = {
  patientId: string
  encounterId: string
  htSession?: string | null
  automedOauthSubject?: string | null
}
export type RecordConditionInteractionApiResponse =
  /** status 200 Confirmation of the interaction recording */ ConditionInteractionResponse
export type RecordConditionInteractionApiArg = {
  htSession?: string | null
  conditionInteractionRequest: ConditionInteractionRequest
}
export type RecordEvidenceInteractionApiResponse =
  /** status 200 Confirmation of the interaction recording */ EvidenceInteractionResponse
export type RecordEvidenceInteractionApiArg = {
  htSession?: string | null
  evidenceInteractionRequest: EvidenceInteractionRequest
}
export type CreateGeneratedNotePrototypeApiResponse =
  /** status 200 The created generated note prototype. */ GeneratedNotePrototypeResponse
export type CreateGeneratedNotePrototypeApiArg = {
  htSession?: string | null
  createGeneratedNotePrototypeRequest: CreateGeneratedNotePrototypeRequest
}
export type GetGeneratedNotePrototypeApiResponse =
  /** status 200 The latest generated note prototype. */ GeneratedNotePrototypeResponse | null
export type GetGeneratedNotePrototypeApiArg = {
  patientId: string
  encounterId: string
  noteType: string
  promptId?: string | null
  htSession?: string | null
}
export type UpdateGeneratedNotePrototypeApiResponse =
  /** status 200 The updated generated note prototype. */ GeneratedNotePrototypeResponse
export type UpdateGeneratedNotePrototypeApiArg = {
  noteId: string
  htSession?: string | null
  updateMaxGeneratedNotePrototypeRequest: UpdateMaxGeneratedNotePrototypeRequest
}
export type RecordNoteGenerationApiResponse = unknown
export type RecordNoteGenerationApiArg = {
  htSession?: string | null
  automedOauthSubject?: string | null
  noteGeneration: NoteGeneration
}
export type SaveGeneratedNoteApiResponse =
  /** status 200 Successful Response */ SaveGeneratedNoteResponse
export type SaveGeneratedNoteApiArg = {
  htSession?: string | null
  saveGeneratedNoteRequest: SaveGeneratedNoteRequest
}
export type V2CreateGeneratedNotePrototypeApiResponse =
  /** status 200 The created generated note prototype. */ GeneratedNotePrototypeResponse
export type V2CreateGeneratedNotePrototypeApiArg = {
  htSession?: string | null
  createGeneratedNotePrototypeRequest: CreateGeneratedNotePrototypeRequest
}
export type V2GetGeneratedNotePrototypeApiResponse =
  /** status 200 The latest generated note prototype. */ GeneratedNotePrototypeResponse | null
export type V2GetGeneratedNotePrototypeApiArg = {
  patientId: string
  encounterId: string
  noteType: string
  htSession?: string | null
}
export type V2UpdateGeneratedNotePrototypeApiResponse =
  /** status 200 The updated generated note prototype. */ GeneratedNotePrototypeResponse
export type V2UpdateGeneratedNotePrototypeApiArg = {
  noteId: string
  htSession?: string | null
  updateGeneratedNotePrototypeRequest: UpdateGeneratedNotePrototypeRequest
}
export type GetGeneratedNoteApiResponse =
  /** status 200 The generated note payload */ NoteResponse
export type GetGeneratedNoteApiArg = {
  baseNoteId: string
  patientId: string
  encounterId: string
  forceRefresh?: boolean
  rebaseNote?: boolean
  htSession?: string | null
  automedOauthSubject?: string | null
}
export type GetGroupPreferencesApiResponse =
  /** status 200 Group preferences, defaulted if no row exists yet */ GroupPreferencesResponse
export type GetGroupPreferencesApiArg = {
  htSession?: string | null
}
export type UpsertGroupPreferencesApiResponse =
  /** status 200 Confirmation of the upsert and the API-visible saved preferences */ GroupPreferencesUpdateResponse
export type UpsertGroupPreferencesApiArg = {
  htSession?: string | null
  groupPreferencesUpdateRequest: GroupPreferencesUpdateRequest
}
export type GetGroupPreferencesAsRegardAdminApiResponse =
  /** status 200 Group preferences, or in-memory defaults if no row exists yet */ GroupPreferencesResponse
export type GetGroupPreferencesAsRegardAdminApiArg = {
  groupId: string
  htSession?: string | null
}
export type UpsertGroupPreferencesAsRegardAdminApiResponse =
  /** status 200 Confirmation of the upsert and the API-visible saved preferences */ GroupPreferencesUpdateResponse
export type UpsertGroupPreferencesAsRegardAdminApiArg = {
  groupId: string
  htSession?: string | null
  groupPreferencesUpdateRequest: GroupPreferencesUpdateRequest
}
export type GetDefaultNoteSectionsApiResponse =
  /** status 200 Default sections for the note type, or an empty list when none are defined */ DefaultNoteSectionsResponse
export type GetDefaultNoteSectionsApiArg = {
  noteType: NoteType2
  htSession?: string | null
}
export type GetSectionTypesApiResponse =
  /** status 200 Every Regard section type */ SectionTypesResponse
export type GetSectionTypesApiArg = {
  htSession?: string | null
}
export type GetUserGeneralPreferencesApiResponse =
  /** status 200 User preferences, defaulted if no row exists yet */ UserGeneralPreferencesResponse
export type GetUserGeneralPreferencesApiArg = {
  htSession?: string | null
}
export type UpsertUserGeneralPreferencesApiResponse =
  /** status 200 Confirmation of the upsert */ UserGeneralPreferencesUpdateResponse
export type UpsertUserGeneralPreferencesApiArg = {
  htSession?: string | null
  userGeneralPreferencesUpdateRequest: UserGeneralPreferencesUpdateRequest
}
export type GetUserProductStateApiResponse =
  /** status 200 User product state */ UserProductStateResponse
export type GetUserProductStateApiArg = {
  htSession?: string | null
}
export type UpsertUserProductStateApiResponse =
  /** status 200 Confirmation of the upsert */ UserProductStateUpdateResponse
export type UpsertUserProductStateApiArg = {
  htSession?: string | null
  userProductStateUpdateRequest: UserProductStateUpdateRequest
}
export type GetUserProductStatesBulkAsRegardAdminApiResponse =
  /** status 200 Map of user_id to has_completed_onboarding */ BulkUserProductStateResponse
export type GetUserProductStatesBulkAsRegardAdminApiArg = {
  htSession?: string | null
  bulkUserProductStateRequest: BulkUserProductStateRequest
}
export type ResetUserOnboardingAsRegardAdminApiResponse =
  /** status 200 Confirmation of the reset */ AdminResetOnboardingResponse
export type ResetUserOnboardingAsRegardAdminApiArg = {
  userId: string
  htSession?: string | null
}
export type GetUserPromptPreferencesApiResponse =
  /** status 200 User prompt preferences, defaulted if no row exists yet */ UserPromptPreferencesResponse
export type GetUserPromptPreferencesApiArg = {
  htSession?: string | null
}
export type UpsertUserPromptPreferencesApiResponse =
  /** status 200 Confirmation of the upsert and the API-visible saved preferences */ UserPromptPreferencesUpdateResponse
export type UpsertUserPromptPreferencesApiArg = {
  htSession?: string | null
  userPromptPreferencesUpdateRequest: UserPromptPreferencesUpdateRequest
}
export type GetAllUserBasePromptsApiResponse =
  /** status 200 User prompt preferences, defaulted if no row exists yet */ AllUserBasePrompts
export type GetAllUserBasePromptsApiArg = {
  htSession?: string | null
}
export type TriggerPatientLoadApiResponse =
  /** status 200 Successful Response */ PatientLoadStatusResponse
export type TriggerPatientLoadApiArg = {
  htSession?: string | null
  patientLoadRequest: PatientLoadRequest
}
export type GetPreOnboardingPreviewApiResponse =
  /** status 200 Successful Response */ PreOnboardingPreviewResponse
export type GetPreOnboardingPreviewApiArg = {
  userId: string
  htSession?: string | null
  automedOauthSubject?: string | null
}
export type TriggerPreOnboardingApiResponse =
  /** status 200 Successful Response */ TriggerPreOnboardingResponse
export type TriggerPreOnboardingApiArg = {
  htSession?: string | null
  automedOauthSubject?: string | null
  triggerPreOnboardingRequest: TriggerPreOnboardingRequest
}
export type GetPreOnboardingStatusApiResponse =
  /** status 200 Successful Response */ PreOnboardingStatusResponse
export type GetPreOnboardingStatusApiArg = {
  userId: string
  htSession?: string | null
}
export type GetUserPreOnboardingStatusApiResponse =
  /** status 200 Successful Response */ UserPreOnboardingStatusResponse
export type GetUserPreOnboardingStatusApiArg = {
  htSession?: string | null
}
export type GetPreOnboardingStatusReportApiResponse =
  /** status 200 Successful Response */ PreOnboardingStatusReportResponse
export type GetPreOnboardingStatusReportApiArg = {
  htSession?: string | null
  statusReportRequest: StatusReportRequest
}
export type VerifyPreOnboardingApiResponse =
  /** status 200 Successful Response */ VerifyPreOnboardingResponse
export type VerifyPreOnboardingApiArg = {
  htSession?: string | null
  verifyPreOnboardingRequest: VerifyPreOnboardingRequest
}
export type GetPreOnboardingBatchProgressApiResponse =
  /** status 200 Successful Response */ BatchProgressResponse
export type GetPreOnboardingBatchProgressApiArg = {
  batchId: string
  htSession?: string | null
}
export type GetPreOnboardingCandidatesReportApiResponse =
  /** status 200 Successful Response */ CandidatesReportResponse
export type GetPreOnboardingCandidatesReportApiArg = {
  htSession?: string | null
  candidatesReportRequest: CandidatesReportRequest
}
export type GetCandidateNotesApiResponse =
  /** status 200 Successful Response */ CandidateNotesResponse
export type GetCandidateNotesApiArg = {
  /** The note type to return candidate notes for. */
  noteType: NoteType2
  /** Max candidate notes to return. */
  limit?: number
  htSession?: string | null
}
export type GetLearnedNotesForNoteTypeApiResponse =
  /** status 200 Successful Response */ LearnedNotesResponse
export type GetLearnedNotesForNoteTypeApiArg = {
  /** The note type whose learned notes to return. */
  noteType: NoteType2
  htSession?: string | null
}
export type LearnFromNoteForNoteTypeApiResponse =
  /** status 200 Successful Response */ LearnFromNoteResponse
export type LearnFromNoteForNoteTypeApiArg = {
  htSession?: string | null
  automedOauthSubject?: string | null
  learnFromNoteRequest: LearnFromNoteRequest
}
export type GetChartNotesApiResponse =
  /** status 200 Successful Response */ ChartNotesResponse
export type GetChartNotesApiArg = {
  /** The note type to list learnable chart notes for. */
  noteType: NoteType2
  /** Max chart notes to return. */
  limit?: number
  htSession?: string | null
}
export type GetChartNoteContentApiResponse =
  /** status 200 Successful Response */ ChartNoteContentResponse
export type GetChartNoteContentApiArg = {
  /** The note type the note would be learned for. */
  noteType: NoteType2
  /** The chart note's source id. */
  sourceId: string
  htSession?: string | null
}
export type RefreshCacheApiResponse =
  /** status 200 Cache refresh result with deletion count */ RefreshCacheResponse
export type RefreshCacheApiArg = {
  /** Patient ID to refresh cache for */
  patientId: string
  htSession?: string | null
}
export type HealthCheckResponse = {
  activeThreadCount: number
  timestamp: string
  version: string
}
export type ChatCitationSource = {
  id?: string | null
  type?: string | null
}
export type ChatCitation = {
  sources: ChatCitationSource[]
  spanId?: string | null
}
export type ChatMessage = {
  turnIndex: number
  role: 'user' | 'assistant'
  text: string
  chatContext: 'moose_homepage' | 'chat_only' | 'note_draft'
  responseType?: ('query_response' | 'note_edit') | null
  terminal?: ('complete' | 'stream_ended_without_complete' | 'error') | null
  citations?: {
    [key: string]: ChatCitation
  } | null
  newContentSubstrings?: string[] | null
}
export type ChatMessageBatch = {
  patientId: string
  encounterId: string
  conversationId: string
  chatId?: string | null
  promptId: string | null
  noteType: string | null
  entryMode: 'ask' | 'generate'
  generatedNoteId?: string | null
  messages: ChatMessage[]
}
export type LineStatus = 'primary' | 'secondary' | 'inNote'
export type NoteLine = {
  id: string
  noteId: string
  text: string
  status: LineStatus
  conditionId: string
  [key: string]: any
}
export type NoteLineStatusResponse = {
  patientId: string
  encounterId: string
  lines: NoteLine[]
  [key: string]: any
}
export type AssociatedConditionDecoration = {
  /** type of this decoration */
  noteDecorationType: 'associatedCondition'
  /** the condition name for this decoration */
  condition: string
}
export type ConditionNameUnderlineDecoration = {
  /** type of this decoration */
  noteDecorationType: 'conditionNameUnderline'
  /** the Regard supported condition module that this decoration represents */
  conditionModule: string
  /** the text of a note line that should be underlined as the condition module */
  lineText: string
}
export type NumberedConditionHeaderDecoration = {
  /** type of this decoration */
  noteDecorationType: 'numberedConditionHeader'
}
export type ObservationInterpretation =
  | 'critical'
  | 'criticallyHigh'
  | 'criticallyLow'
  | 'high'
  | 'low'
  | 'normal'
  | 'abnormal'
export type ObservationInterpretationDecoration = {
  /** type of this decoration */
  noteDecorationType: 'observationInterpretation'
  /** the observation interpretation for this decoration */
  observationInterpretation: ObservationInterpretation
}
export type NoteDecoration =
  | ({
      noteDecorationType: 'associatedCondition'
    } & AssociatedConditionDecoration)
  | ({
      noteDecorationType: 'conditionNameUnderline'
    } & ConditionNameUnderlineDecoration)
  | ({
      noteDecorationType: 'numberedConditionHeader'
    } & NumberedConditionHeaderDecoration)
  | ({
      noteDecorationType: 'observationInterpretation'
    } & ObservationInterpretationDecoration)
export type RegardConditionSourceBullet = {
  /** unique identifier for this bullet */
  id: string
  /** the text content of the bullet */
  text: string
  /** decorations including AssociatedConditionDecoration and ObservationInterpretationDecoration */
  associatedDecorations?: NoteDecoration[]
  /** the condition module this bullet is associated with */
  module: string
}
export type MaxRequiredContent = {
  meshingConditionSourceBullets: RegardConditionSourceBullet[]
  conditionsToFilteredBulletIds: {
    [key: string]: string[]
  }
  conditionsToSecondaryEvidenceBulletIds?: {
    [key: string]: string[]
  }
  conditionsToExistingUnstyledBulletTexts?: {
    [key: string]: string[]
  }
  [key: string]: any
}
export type LineStatusUpdateResponse = {
  updateSuccess: boolean
  [key: string]: any
}
export type LineStatusUpdateRequest = {
  patientId: string
  encounterId: string
  line: NoteLine
  [key: string]: any
}
export type SaveConditionDismissalsResponse = {
  success: boolean
  [key: string]: any
}
export type DismissalItem = {
  conditionModule: string
  conditionHeader: string
  isDismissed: boolean
  [key: string]: any
}
export type SaveConditionDismissalsRequest = {
  patientId: string
  encounterId: string
  conditions: DismissalItem[]
  [key: string]: any
}
export type ActiveDismissalItem = {
  conditionModule: string
  conditionHeader: string
  lastModifiedBy: string
  lastModifiedByName: string
  dismissedAt: string
  [key: string]: any
}
export type GetConditionDismissalsResponse = {
  conditions: ActiveDismissalItem[]
  [key: string]: any
}
export type DemoResetResponse = {
  status: string
  [key: string]: any
}
export type ValidationError = {
  loc: (string | number)[]
  msg: string
  type: string
  input?: any
  ctx?: object
}
export type HttpValidationError = {
  detail?: ValidationError[]
}
export type ConditionCategory =
  | 'excludedByMax'
  | 'dismissedByUser'
  | 'shelved'
  | 'inNote'
export type ConditionStatus = 'new' | 'existing'
export type EditorCondition = {
  id: string
  conditionHeader: string
  conditionCategory: ConditionCategory
  conditionStatus: ConditionStatus
  author?: string | null
  [key: string]: any
}
export type ConditionStatusResponse = {
  encounterId: string
  conditions: EditorCondition[]
  [key: string]: any
}
export type ConditionStatusUpdateResponse = {
  updateSuccess: boolean
  [key: string]: any
}
export type UpdateEditorCondition = {
  id: string
  conditionHeader: string
  conditionCategory: ConditionCategory
  [key: string]: any
}
export type ConditionStatusUpdateRequest = {
  patientId: string
  baseNoteId: string
  encounterId: string
  condition: UpdateEditorCondition
  [key: string]: any
}
export type ConditionKeyword = {
  text: string
  isCaseSensitive: boolean
  shouldLiveDx: boolean
  [key: string]: any
}
export type ConditionSynonym = {
  conditionKey: string
  conditionName: string
  keywords: ConditionKeyword[]
  [key: string]: any
}
export type ConditionSynonyms = {
  conditions: ConditionSynonym[]
  [key: string]: any
}
export type ConditionTitlePrefix =
  | 'hashtag'
  | 'number'
  | 'none'
  | 'inconclusive'
export type ConditionTitleBold = 'bold' | 'not bold' | 'inconclusive'
export type ConditionTitleItalics = 'italics' | 'not italics' | 'inconclusive'
export type ConditionTitleCapitalization =
  | 'first letter'
  | 'title case'
  | 'all letters'
  | 'none'
  | 'inconclusive'
export type EvidenceLinePrefix =
  | 'space'
  | 'dash'
  | 'dash and space'
  | 'none'
  | 'inconclusive'
export type EvidenceLineCapitalization = 'capital' | 'small' | 'inconclusive'
export type DateFormat =
  | 'MM/dd/yyyy'
  | 'MM/dd/yy'
  | 'MM/dd'
  | 'MM-dd'
  | 'MM-dd-yyyy'
  | 'MM-dd-yy'
  | 'M/d/yyyy'
  | 'M/d/yy'
  | 'M/d'
  | 'M-d'
  | 'M-d-yyyy'
  | 'M-d-yy'
  | 'inconclusive'
export type DateInclusion = 'include' | 'exclude' | 'inconclusive'
export type NoteStyle = {
  conditionTitlePrefix: ConditionTitlePrefix
  conditionTitleBold: ConditionTitleBold
  conditionTitleItalics: ConditionTitleItalics
  conditionTitleCapitalization: ConditionTitleCapitalization
  evidenceLinePrefix: EvidenceLinePrefix
  evidenceLineCapitalization: EvidenceLineCapitalization
  dateFormat: DateFormat
  medsDateInclusion: DateInclusion
  labsDateInclusion: DateInclusion
  vitalsDateInclusion: DateInclusion
  vitalsTimeInclusion: DateInclusion
  [key: string]: any
}
export type NoteStyleResponse = {
  noteStyle: NoteStyle
  [key: string]: any
}
export type NoteStyleUpdateResponse = {
  updateSuccess: boolean
  [key: string]: any
}
export type NoteStyleUpdateRequest = {
  noteStyle: NoteStyle
  [key: string]: any
}
export type DiagnosedAtResponse = {
  diagnosedAt: string | null
  [key: string]: any
}
export type BulletTag =
  | 'info'
  | 'medication'
  | 'observation'
  | 'procedure'
  | 'impression'
  | 'riskFactors'
  | 'riskCalculation'
export type Bullet = {
  tags: BulletTag[]
  item: any[]
  id: string
  displayType: string
  interpretation: string
  observationKey: string
  panelName: string
  observations: any[]
  hideTitle: boolean
  abbreviate: boolean
  displays: {
    [key: string]: string
  }
  procedures: any[]
  exam: any
  findings: string[]
  factors: any[]
  [key: string]: any
}
export type AssessmentPlan = {
  module: string
  title: any[]
  assessment: Bullet[]
  plan: Bullet[]
  general: Bullet[]
  meds: any[]
  negative: boolean
  positive: boolean
  severity: any | null
  tableData: any | null
  sourceNotes: any[]
  parentModule: string | null
  ungroupedPlan: any[]
  sourceConditions: any[]
  supportingEvidence: any[]
  ungroupedAssessment: any[]
  disabledByFeatureFlag: boolean
  dxLocatorRegexPattern: string
  codes: {
    [key: string]: string[]
  }
  [key: string]: any
}
export type ConditionResult = {
  kind: string
  positive: boolean
  considerPositive?: boolean
  assessmentPlan: AssessmentPlan
  dxLocatorRegexPattern: string
  parentModule: string | null
  disabledByFeatureFlag: boolean
  recommendDisplay?: boolean
  [key: string]: any
}
export type ConditionResultsResponse = {
  conditionResults: {
    [key: string]: ConditionResult
  }
  [key: string]: any
}
export type BaseNote = {
  id: string
  baseNoteType: string
  abbreviatedNoteType: string
  kindKey: string
  kindKeys: string[]
  author: string
  authorId: string
  authenticatorId: string
  status: string
  effective: string
  lastUpdated: string
  hasDraft: boolean
  currentEncounter: boolean
  encounterId: string
  isMapperBaseNote?: boolean
  contentHash: string
  html: string
  [key: string]: any
}
export type ExcludedBaseNote = {
  id: string
  reason: string
  [key: string]: any
}
export type CyobNoteOptionsResponse = {
  baseNoteOptions: BaseNote[]
  excludedBaseNotes: ExcludedBaseNote[]
  [key: string]: any
}
export type ConditionInteractionResponse = {
  success: boolean
  [key: string]: any
}
export type AppVersion = 'legacy' | 'v1' | 'v2'
export type ConditionInteractionType =
  | 'added_from_dismissed'
  | 'added_from_excluded_by_max'
  | 'dismissed'
export type ConditionInteractionRequest = {
  appVersion: AppVersion
  actorId: string
  encounterId: string
  action: ConditionInteractionType
  condition: string
  [key: string]: any
}
export type EvidenceInteractionResponse = {
  success: boolean
  [key: string]: any
}
export type EvidenceInteractionType =
  | 'added_from_below_line'
  | 'added_from_dxd'
  | 'dismissed'
  | 'deleted'
export type EvidenceInteractionRequest = {
  appVersion: AppVersion
  actorId: string
  encounterId: string
  action: EvidenceInteractionType
  /** Note: bullet_ids are not a high fidelity source of truth, but are being recorded as extra information for now */
  bulletId: string
  condition: string
  evidenceLine: string
  [key: string]: any
}
export type GeneratedNotePrototypeResponse = {
  id: string
  userId: string
  patientId: string
  encounterId: string
  adminSave: boolean
  previousNoteId: string | null
  notePrompt: string | null
  promptId?: string | null
  noteType: string
  noteContent: string
  originalNoteContent?: string | null
  physicianEditSegments?: string[]
  citationSources?: {
    [key: string]: any
  } | null
  spans?: any[] | null
  diffState?: {
    [key: string]: any
  } | null
  isBaseNote?: boolean
  baseNoteSameAuthor?: boolean | null
  createdAt: string
  updatedAt: string
  lastGeneratedAt?: string | null
  [key: string]: any
}
export type CreateGeneratedNotePrototypeRequest = {
  patientId: string
  encounterId: string
  previousNoteId?: string | null
  notePrompt?: string | null
  promptId?: string | null
  noteType: string
  noteContent: string
  citationSources?: {
    [key: string]: any
  } | null
  spans?: any[] | null
  diffState?: {
    [key: string]: any
  } | null
  [key: string]: any
}
export type UpdateMaxGeneratedNotePrototypeRequest = {
  noteContent: string
  originalNoteContent?: string | null
  diffState?: {
    [key: string]: any
  } | null
  notePrompt?: string | null
  [key: string]: any
}
export type NoteGeneration = {
  patientId: string
  encounterId: string
  generatedNoteId?: string | null
  kind:
    | 'generate'
    | 'update'
    | 'generate_from_base'
    | 'chat_edit'
    | 'chat_reply'
  contentMd: string
  snapshotVersion: string | null
  spans: {
    [key: string]: any
  }[]
  newContentSubstrings?: string[] | null
  changeSummary?: {
    [key: string]: any
  } | null
  excludedConditions?:
    | {
        [key: string]: any
      }[]
    | null
  citationSources: {
    [key: string]: string
  }
  excludedEvidence?: {
    [key: string]: string[]
  } | null
}
export type SaveGeneratedNoteResponse = {
  success: boolean
  taskId?: string | null
  [key: string]: any
}
export type SaveGeneratedNoteRequest = {
  generationId: string
  baseNoteId: string
  patientId: string
  encounterId: string
  notePayload: {
    [key: string]: any
  }
  conditionsToFilteredBulletIds: {
    [key: string]: string[]
  }
  conditionsToSecondaryEvidenceBulletIds: {
    [key: string]: string[]
  }
  regardConditionSourceBullets: RegardConditionSourceBullet[]
  contentHash?: string
  generatedPayloadHash?: string
  [key: string]: any
}
export type UpdateGeneratedNotePrototypeRequest = {
  noteContent: string
  originalNoteContent?: string | null
  diffState?: {
    [key: string]: any
  } | null
  [key: string]: any
}
export type NoteStatus = 'draft' | 'final' | 'base'
export type NoteType = 'historyPhysical' | 'progress'
export type NoteSectionType =
  | 'assessmentPlan'
  | 'twentyFourHourCourse'
  | 'subjective'
  | 'chiefComplaint'
  | 'historyOfPresentIllness'
  | 'physicalExam'
export type NoteSubsectionType =
  | 'conditionSubsection'
  | 'shelfSubsection'
  | 'footerSubsection'
  | 'oneLinerSubsection'
  | 'genericSubsection'
export type NoteBlockType = 'freetextBlock'
export type NoteLineType = 'freetextLine'
export type NoteLineSource =
  | 'baseNote'
  | 'regardAdded'
  | 'regardUpdated'
  | 'scribeAdded'
  | 'scribeUpdated'
  | 'userAdded'
export type MesherConcept = {
  /** The clinical concept's unique identifier. */
  id: string
  /** The type of clinical concept. */
  type: string
  /** The label of the clinical concept. */
  label: string
}
export type MesherIdentifiedConcept = {
  /** The concept that was mentioned. */
  concept: MesherConcept
  /** The text that was matched in the source document. */
  matchedText: string
  /** The location of the matched text in the source document. */
  span: [number, number]
  /** Whether the condition is supported in regard dxs */
  regardSupported?: boolean
}
export type NoteLine2 = {
  /** type of this line */
  noteLineType: NoteLineType
  /** the id of this line */
  id: string
  /** datetime that this line was created */
  createdDatetime: string
  /** user editable html string content of this line */
  html: string
  /** the source type of this line */
  source: NoteLineSource
  /** the decorations on this line */
  decorations: NoteDecoration[]
  /** whether this line has been dismissed */
  dismissed: boolean
  /** whether this line is in the note */
  inNote: boolean
  /** whether this line is a condition header */
  conditionHeader: boolean
  /** condition concepts identified for this line */
  conditionConcepts?: MesherIdentifiedConcept[]
  /** list of regard dx bullet texts the line is composed of (either directly or that have meshed into it) */
  unstyledBulletTexts?: string[]
}
export type NoteBlock = {
  /** type of this note block */
  noteBlockType: NoteBlockType
  /** id of this note block */
  id: string
  /** the note lines in this note block, in the order of how they appear */
  lines: NoteLine2[]
  /** whether this block has been dismissed */
  dismissed: boolean
}
export type NoteSubsection = {
  /** type of this subsection */
  noteSubsectionType: NoteSubsectionType
  /** the id of this subsection */
  id: string
  /** the note blocks in this note subsection, in the order of how they appear */
  blocks: NoteBlock[]
}
export type NoteSection = {
  /** type of this section */
  noteSectionType: NoteSectionType
  /** the id of this section */
  id: string
  /** the subsections in this section, in the order of how they appear */
  subsections: NoteSubsection[]
}
export type NotePayload = {
  /** type of this note */
  noteType: NoteType
  /** the note sections in this Note, in the order of how they appear */
  sections: NoteSection[]
  /** the id of this Note */
  id: string
}
export type Note = {
  id: number
  version: string
  userId: string
  status: NoteStatus
  encounterId: string
  practitionerNoteId: string
  previousNoteId: number | null
  previousNoteVersion: string | null
  adminSave: boolean
  basePractitionerNoteContentHash: string
  notePayload: NotePayload
  sources: string[]
  regardConditionSourceBullets: RegardConditionSourceBullet[]
}
export type LineVersion = {
  version: string
  line: NoteLine2
}
export type LineVersionHistory = {
  lineVersions: {
    [key: string]: LineVersion[]
  }
  currentTimestamp: string
}
export type NoteVersionHistory = {
  lineVersionHistory: LineVersionHistory
  /** A mapping of line ids to the most recent user attributed line version, if one such exists for the line. */
  mostRecentUserAttributedLineVersions: {
    [key: string]: LineVersion
  }
}
export type NoteResponse = {
  note: Note
  noteVersionHistory?: NoteVersionHistory | null
  conditionsToFilteredBulletIds?: {
    [key: string]: string[]
  } | null
  conditionsToSecondaryEvidenceBulletIds?: {
    [key: string]: string[]
  } | null
  baseNoteHasUpdates?: boolean
  generationId?: string
  generatedPayloadHash?: string | null
}
export type NoteType2 =
  | 'progress-note'
  | 'history-physical'
  | 'discharge-summary'
  | 'attestation'
  | 'other'
  | 'consult'
  | 'procedure-note'
  | 'op-note'
  | 'annual-wellness-visit'
  | 'sick-visit'
  | 'follow-up'
  | 'icu-hp'
  | 'icu-progress-note'
  | 'hospital-course-standalone'
  | 'icu-step-down-transfer'
  | 'sign-out'
  | 'unknown'
export type Section = {
  title: string
  description: string
  sectionType?: string | null
  systemDefaults?: string | null
  recognitionContext?: string | null
  adminOverrides?: string[]
}
export type NoteStructurePreferences = {
  sections?: Section[]
  freeTextDirectives?: string[]
}
export type LearnedFromSource = {
  pastedAt: string
}
export type GroupPrompt = {
  id: string
  noteType: NoteType2
  buttonName: string
  isRequired: boolean
  content?: string
  structure?: NoteStructurePreferences
  learnedFrom?: LearnedFromSource | null
  isDeleted?: boolean
}
export type PromptPermissions = {
  allowCustomPrompts: boolean
}
export type Specialty =
  | 'hospital_medicine'
  | 'critical_care'
  | 'cardiology'
  | 'case_management'
  | 'cardiothoracic_surgery'
  | 'general_surgery'
  | 'hospice_palliative_medicine'
  | 'infectious_disease'
  | 'oncology'
  | 'orthopedic_surgery'
  | 'pulmonology'
  | 'emergency_medicine'
  | 'nephrology'
  | 'neurology'
export type GroupPreferences = {
  groupPrompts: GroupPrompt[]
  promptPermissions: PromptPermissions
  primarySpecialty?: Specialty | null
}
export type GroupPreferencesResponse = {
  preferences: GroupPreferences
  [key: string]: any
}
export type GroupPreferencesUpdateResponse = {
  updateSuccess: boolean
  preferences: GroupPreferences
  [key: string]: any
}
export type GroupPreferencesUpdateRequest = {
  preferences: GroupPreferences
  [key: string]: any
}
export type DefaultNoteSectionsResponse = {
  sections: Section[]
  [key: string]: any
}
export type SectionTypeDefinition = {
  key: string
  label: string
  description: string
  recommendedNoteTypes: NoteType2[]
}
export type SectionTypesResponse = {
  sectionTypes: SectionTypeDefinition[]
  [key: string]: any
}
export type DateFormat2 =
  | 'MM/DD/YYYY'
  | 'MM/DD/YY'
  | 'MM/DD'
  | 'MM-DD-YYYY'
  | 'MM-DD-YY'
  | 'MM-DD'
export type MedicationNaming = 'Generic names' | 'Brand names'
export type MedicationDetails = 'Include dose and route' | 'Name only'
export type PendingLabsDisplay = 'Show' | 'Hide' | 'Show with (pending) flag'
export type UnfinalizedImagingReads =
  | 'Include'
  | 'Exclude'
  | 'Include with (preliminary) flag'
export type GlobalPreferences = {
  dateFormat: DateFormat2
  includeLeadingZeros: boolean
  medicationNaming: MedicationNaming
  medicationDetails: MedicationDetails
  pendingLabsDisplay: PendingLabsDisplay
  unfinalizedImagingReads: UnfinalizedImagingReads
}
export type EvidenceLineListStyle =
  | 'Dash only'
  | 'Space only'
  | 'Dash and space'
  | 'No prefix'
export type EvidenceLineCapitalization2 =
  | 'As written'
  | 'Capitalize first letter'
  | 'lowercase'
export type ConditionTitlePrefix2 =
  | 'None'
  | 'Hashtag (#)'
  | 'Numbered (1., 2., 3.)'
export type ConditionTitleCapitalization2 =
  | 'As written'
  | 'Title Case'
  | 'Sentence case'
  | 'UPPERCASE'
  | 'lowercase'
export type MedicationFormat = 'Single line' | 'Multi-line (one per line)'
export type NotePreferences = {
  evidenceLineListStyle: EvidenceLineListStyle
  evidenceLineCapitalization: EvidenceLineCapitalization2
  conditionTitlePrefix: ConditionTitlePrefix2
  conditionTitleBold: boolean
  conditionTitleItalic: boolean
  conditionTitleUnderline: boolean
  conditionTitleCapitalization: ConditionTitleCapitalization2
  medicationFormat: MedicationFormat
  includeLabValuesInAp: boolean
  includeVitalsInAp: boolean
  includeRiskScores: boolean
  showDatesForLabsImaging: boolean
  freeTextDirectives?: string[]
  noteStructurePreferences?: NoteStructurePreferences | null
}
export type DischargeSummaryMedicationFormat =
  | 'Name + dose + frequency'
  | 'Name + dose only'
  | 'Name only'
export type FollowUpFormat =
  | 'List with timeframes'
  | 'List with dates'
  | 'Narrative'
export type DischargeSummaryPreferences = {
  conditionTitlePrefix: ConditionTitlePrefix2
  conditionTitleBold: boolean
  conditionTitleItalic: boolean
  conditionTitleUnderline: boolean
  conditionTitleCapitalization: ConditionTitleCapitalization2
  medicationFormat: DischargeSummaryMedicationFormat
  highlightMedicationChanges: boolean
  includePendingResults: boolean
  followUpFormat: FollowUpFormat
  freeTextDirectives?: string[]
  noteStructurePreferences?: NoteStructurePreferences | null
}
export type AttestationPreferences = {
  agreementPhrasing?: string
  freeTextDirectives?: string[]
  noteStructurePreferences?: NoteStructurePreferences | null
}
export type ConsultPreferences = {
  conditionTitlePrefix: ConditionTitlePrefix2
  conditionTitleBold: boolean
  conditionTitleItalic: boolean
  conditionTitleUnderline: boolean
  conditionTitleCapitalization: ConditionTitleCapitalization2
  evidenceLineListStyle: EvidenceLineListStyle
  evidenceLineCapitalization: EvidenceLineCapitalization2
  medicationFormat: MedicationFormat
  freeTextDirectives?: string[]
  noteStructurePreferences?: NoteStructurePreferences | null
}
export type LearnedFromPreferences = {
  progressNote?: LearnedFromSource | null
  historyAndPhysical?: LearnedFromSource | null
  dischargeSummary?: LearnedFromSource | null
  attestation?: LearnedFromSource | null
  consult?: LearnedFromSource | null
  icuProgressNote?: LearnedFromSource | null
  icuHistoryAndPhysical?: LearnedFromSource | null
  hospitalCourseStandalone?: LearnedFromSource | null
  icuStepDownTransfer?: LearnedFromSource | null
  signOut?: LearnedFromSource | null
}
export type ConfigSource = 'user_settings'
export type ConfigSourcePreferences = {
  progressNote?: ConfigSource | null
  historyAndPhysical?: ConfigSource | null
  dischargeSummary?: ConfigSource | null
  attestation?: ConfigSource | null
  consult?: ConfigSource | null
  icuProgressNote?: ConfigSource | null
  icuHistoryAndPhysical?: ConfigSource | null
  hospitalCourseStandalone?: ConfigSource | null
  icuStepDownTransfer?: ConfigSource | null
  signOut?: ConfigSource | null
}
export type UserGeneralPreferences = {
  globalPreferences?: GlobalPreferences | null
  notePreferences?: NotePreferences | null
  dischargeSummaryPreferences?: DischargeSummaryPreferences | null
  historyAndPhysicalPreferences?: NotePreferences | null
  attestationPreferences?: AttestationPreferences | null
  consultPreferences?: ConsultPreferences | null
  icuHistoryAndPhysicalPreferences?: NotePreferences | null
  icuProgressNotePreferences?: NotePreferences | null
  hospitalCourseStandalonePreferences?: NotePreferences | null
  icuStepDownTransferPreferences?: NotePreferences | null
  signOutPreferences?: NotePreferences | null
  learnedFrom?: LearnedFromPreferences | null
  configSource?: ConfigSourcePreferences | null
}
export type UserGeneralPreferencesResponse = {
  preferences: UserGeneralPreferences
}
export type UserGeneralPreferencesUpdateResponse = {
  updateSuccess: boolean
}
export type UserGeneralPreferencesUpdateRequest = {
  preferences: UserGeneralPreferences
}
export type UserProductStateResponse = {
  hasCompletedOnboarding: boolean
  isEligibleForAdminOnboarding: boolean
  [key: string]: any
}
export type UserProductStateUpdateResponse = {
  updateSuccess: boolean
  [key: string]: any
}
export type UserProductStateUpdateRequest = {
  hasCompletedOnboarding: boolean
  hasCompletedAdminOnboarding?: boolean
  [key: string]: any
}
export type BulkUserProductStateResponse = {
  states: {
    [key: string]: boolean
  }
  [key: string]: any
}
export type BulkUserProductStateRequest = {
  userIds: string[]
  [key: string]: any
}
export type AdminResetOnboardingResponse = {
  resetSuccess: boolean
  [key: string]: any
}
export type PersonalPrompt = {
  id: string
  noteType: NoteType2
  buttonName: string
  content: string
  isDeleted?: boolean
}
export type UserPromptPreferences = {
  personalPrompts: PersonalPrompt[]
  enabledGroupPromptIds: string[]
}
export type UserPromptPreferencesResponse = {
  preferences: UserPromptPreferences
}
export type UserPromptPreferencesUpdateResponse = {
  updateSuccess: boolean
  preferences: UserPromptPreferences
}
export type UserPromptPreferencesUpdateRequest = {
  preferences: UserPromptPreferences
}
export type AllUserBasePrompts = {
  prompts: {
    [key: string]: string | null
  }
}
export type PatientLoadStatusResponse = {
  status: 'needs-load' | 'loading' | 'up-to-date' | 'failed'
  diagnosedAt?: string | null
  [key: string]: any
}
export type PatientLoadRequest = {
  patientId: string
  requestDatetime?: string | null
  recencyThresholdDatetime?: string | null
  appLaunchId?: string | null
  [key: string]: any
}
export type NoteTypeStatus = 'derived' | 'no_notes' | 'failed'
export type SelectedNoteResponse = {
  sourceId: string
  effectiveAt: string | null
  patientId: string
  noteKindLabels: string[]
  [key: string]: any
}
export type RegardNoteGenModelsUserPreferencesDateFormat =
  | 'MM/DD/YYYY'
  | 'MM/DD/YY'
  | 'MM/DD'
  | 'MM-DD-YYYY'
  | 'MM-DD-YY'
  | 'MM-DD'
export type GlobalPreferences2 = {
  dateFormat: RegardNoteGenModelsUserPreferencesDateFormat
  includeLeadingZeros: boolean
  medicationNaming: MedicationNaming
  medicationDetails: MedicationDetails
  pendingLabsDisplay: PendingLabsDisplay
  unfinalizedImagingReads: UnfinalizedImagingReads
  additional_properties?: {
    [key: string]: any
  }
}
export type RegardNoteGenModelsUserPreferencesEvidenceLineCapitalization =
  | 'As written'
  | 'Capitalize first letter'
  | 'lowercase'
export type RegardNoteGenModelsUserPreferencesConditionTitlePrefix =
  | 'None'
  | 'Hashtag (#)'
  | 'Numbered (1., 2., 3.)'
export type RegardNoteGenModelsUserPreferencesConditionTitleCapitalization =
  | 'As written'
  | 'Title Case'
  | 'Sentence case'
  | 'UPPERCASE'
  | 'lowercase'
export type Section2 = {
  title: string
  description: string
  sectionType?: string | null
  systemDefaults?: string | null
  recognitionContext?: string | null
  adminOverrides?: string[] | null
  additional_properties?: {
    [key: string]: any
  }
}
export type NoteStructurePreferences2 = {
  sections?: Section2[] | null
  freeTextDirectives?: string[] | null
  additional_properties?: {
    [key: string]: any
  }
}
export type NotePreferences2 = {
  evidenceLineListStyle: EvidenceLineListStyle
  evidenceLineCapitalization: RegardNoteGenModelsUserPreferencesEvidenceLineCapitalization
  conditionTitlePrefix: RegardNoteGenModelsUserPreferencesConditionTitlePrefix
  conditionTitleBold: boolean
  conditionTitleItalic: boolean
  conditionTitleUnderline: boolean
  conditionTitleCapitalization: RegardNoteGenModelsUserPreferencesConditionTitleCapitalization
  medicationFormat: MedicationFormat
  includeLabValuesInAp: boolean
  includeVitalsInAp: boolean
  includeRiskScores: boolean
  showDatesForLabsImaging: boolean
  freeTextDirectives?: string[] | null
  noteStructurePreferences?: NoteStructurePreferences2 | null
  additional_properties?: {
    [key: string]: any
  }
}
export type DischargeSummaryPreferences2 = {
  conditionTitlePrefix: RegardNoteGenModelsUserPreferencesConditionTitlePrefix
  conditionTitleBold: boolean
  conditionTitleItalic: boolean
  conditionTitleUnderline: boolean
  conditionTitleCapitalization: RegardNoteGenModelsUserPreferencesConditionTitleCapitalization
  medicationFormat: DischargeSummaryMedicationFormat
  highlightMedicationChanges: boolean
  includePendingResults: boolean
  followUpFormat: FollowUpFormat
  freeTextDirectives?: string[] | null
  noteStructurePreferences?: NoteStructurePreferences2 | null
  additional_properties?: {
    [key: string]: any
  }
}
export type AttestationPreferences2 = {
  agreementPhrasing?: string | null
  freeTextDirectives?: string[] | null
  noteStructurePreferences?: NoteStructurePreferences2 | null
  additional_properties?: {
    [key: string]: any
  }
}
export type ConsultPreferences2 = {
  conditionTitlePrefix: RegardNoteGenModelsUserPreferencesConditionTitlePrefix
  conditionTitleBold: boolean
  conditionTitleItalic: boolean
  conditionTitleUnderline: boolean
  conditionTitleCapitalization: RegardNoteGenModelsUserPreferencesConditionTitleCapitalization
  evidenceLineListStyle: EvidenceLineListStyle
  evidenceLineCapitalization: RegardNoteGenModelsUserPreferencesEvidenceLineCapitalization
  medicationFormat: MedicationFormat
  freeTextDirectives?: string[] | null
  noteStructurePreferences?: NoteStructurePreferences2 | null
  additional_properties?: {
    [key: string]: any
  }
}
export type LearnedFromSource2 = {
  pastedAt: string
  additional_properties?: {
    [key: string]: any
  }
}
export type LearnedFromPreferences2 = {
  progressNote?: LearnedFromSource2 | null
  historyAndPhysical?: LearnedFromSource2 | null
  dischargeSummary?: LearnedFromSource2 | null
  attestation?: LearnedFromSource2 | null
  consult?: LearnedFromSource2 | null
  icuProgressNote?: LearnedFromSource2 | null
  icuHistoryAndPhysical?: LearnedFromSource2 | null
  hospitalCourseStandalone?: LearnedFromSource2 | null
  icuStepDownTransfer?: LearnedFromSource2 | null
  signOut?: LearnedFromSource2 | null
  additional_properties?: {
    [key: string]: any
  }
}
export type ConfigSource2 = 'user_settings'
export type ConfigSourcePreferences2 = {
  progressNote?: ConfigSource2 | null
  historyAndPhysical?: ConfigSource2 | null
  dischargeSummary?: ConfigSource2 | null
  attestation?: ConfigSource2 | null
  consult?: ConfigSource2 | null
  icuProgressNote?: ConfigSource2 | null
  icuHistoryAndPhysical?: ConfigSource2 | null
  hospitalCourseStandalone?: ConfigSource2 | null
  icuStepDownTransfer?: ConfigSource2 | null
  signOut?: ConfigSource2 | null
  additional_properties?: {
    [key: string]: any
  }
}
export type UserGeneralPreferences2 = {
  globalPreferences?: GlobalPreferences2 | null
  notePreferences?: NotePreferences2 | null
  dischargeSummaryPreferences?: DischargeSummaryPreferences2 | null
  historyAndPhysicalPreferences?: NotePreferences2 | null
  attestationPreferences?: AttestationPreferences2 | null
  consultPreferences?: ConsultPreferences2 | null
  icuHistoryAndPhysicalPreferences?: NotePreferences2 | null
  icuProgressNotePreferences?: NotePreferences2 | null
  hospitalCourseStandalonePreferences?: NotePreferences2 | null
  icuStepDownTransferPreferences?: NotePreferences2 | null
  signOutPreferences?: NotePreferences2 | null
  learnedFrom?: LearnedFromPreferences2 | null
  configSource?: ConfigSourcePreferences2 | null
  additional_properties?: {
    [key: string]: any
  }
}
export type NoteTypePreviewResponse = {
  noteType: NoteType2
  status: NoteTypeStatus
  intakerReturned: number
  passedFilters: number
  selectedNotes: SelectedNoteResponse[]
  derivedPreferences: UserGeneralPreferences2 | null
  [key: string]: any
}
export type PreOnboardingPreviewResponse = {
  userId: string
  results: NoteTypePreviewResponse[]
  [key: string]: any
}
export type TriggerPreOnboardingResponse = {
  batchId: string
  enqueued: number
  [key: string]: any
}
export type PreOnboardingScope =
  | 'new_users_only'
  | 'all_users'
  | 'onboarding_1_0_users'
export type TriggerPreOnboardingRequest = {
  userIds: string[]
  scope?: PreOnboardingScope
  [key: string]: any
}
export type PreOnboardingStatus =
  | 'not_pre_learned'
  | 'pre_learned'
  | 'verified'
  | 'no_prior_notes'
export type NoteTypeStatusResponse = {
  noteType: NoteType2
  status: PreOnboardingStatus
  sourceNoteIds: string[] | null
  batchId: string | null
  [key: string]: any
}
export type PreOnboardingStatusResponse = {
  userId: string
  statuses: NoteTypeStatusResponse[]
  [key: string]: any
}
export type UserPreOnboardingStatusResponse = {
  userId: string
  statuses: NoteTypeStatusResponse[]
  isBrandNew: boolean
  setUpNoteTypes: NoteType2[]
  [key: string]: any
}
export type PreOnboardingStatusReportResponse = {
  reports: PreOnboardingStatusResponse[]
  [key: string]: any
}
export type StatusReportRequest = {
  userIds: string[]
  [key: string]: any
}
export type VerifyPreOnboardingResponse = {
  verified: boolean
  [key: string]: any
}
export type VerifyPreOnboardingRequest = {
  noteType: NoteType2
  [key: string]: any
}
export type BatchProgressResponse = {
  batchId: string
  succeeded: number
  failed: number
  skipped: number
  [key: string]: any
}
export type NoteTypeCandidatesReport = {
  noteType: NoteType2
  intakerReturned: number
  passedFilters: number
  selectedNotes: SelectedNoteResponse[]
  [key: string]: any
}
export type UserCandidatesReport = {
  userId: string
  practitionerIds: string[]
  noteTypes: NoteTypeCandidatesReport[]
  error?: string | null
  [key: string]: any
}
export type CandidatesReportResponse = {
  reports: UserCandidatesReport[]
  [key: string]: any
}
export type CandidatesReportRequest = {
  userIds: string[]
  [key: string]: any
}
export type CandidateNotesResponse = {
  noteType: NoteType2
  selectedNotes: SelectedNoteResponse[]
  [key: string]: any
}
export type LearnedNoteOrigin = 'auto' | 'chosen' | 'pasted'
export type LearnedNote = {
  origin: LearnedNoteOrigin
  sourceId: string | null
  effectiveAt: string | null
  learnedAt: string
}
export type LearnedNotesResponse = {
  noteType: NoteType2
  learnedNotes: LearnedNote[]
}
export type LearnFromNoteResponse = {
  preferences: UserGeneralPreferences
  learnedNotes: LearnedNote[]
}
export type LearnFromNoteRequest = {
  noteType: NoteType2
  sourceId?: string | null
  pastedText?: string | null
}
export type ChartNote = {
  sourceId: string
  effectiveAt: string | null
  isLearned: boolean
}
export type ChartNotesResponse = {
  noteType: NoteType2
  chartNotes: ChartNote[]
}
export type ChartNoteContentResponse = {
  text: string
}
export type RefreshCacheResponse = {
  success: boolean
  keysDeleted: number
  errors?: string[]
  [key: string]: any
}
export const {
  useHealthcheckQuery,
  useLazyHealthcheckQuery,
  useUpsertChatMessagesMutation,
  useGetLineStatusesMutation,
  useUpdateLineStatusMutation,
  useSaveConditionDismissalsMutation,
  useGetConditionDismissalsQuery,
  useLazyGetConditionDismissalsQuery,
  useResetDemoDatabaseMutation,
  useResetDemoDatabaseForServiceMutation,
  useGetConditionStatusesQuery,
  useLazyGetConditionStatusesQuery,
  useUpdateConditionStatusMutation,
  useGetConditionSynonymsQuery,
  useLazyGetConditionSynonymsQuery,
  useGetNoteStyleQuery,
  useLazyGetNoteStyleQuery,
  useUpdateNoteStyleMutation,
  useGetDiagnosedAtQuery,
  useLazyGetDiagnosedAtQuery,
  useGetConditionResultsQuery,
  useLazyGetConditionResultsQuery,
  useGetCyobNoteOptionsQuery,
  useLazyGetCyobNoteOptionsQuery,
  useRecordConditionInteractionMutation,
  useRecordEvidenceInteractionMutation,
  useCreateGeneratedNotePrototypeMutation,
  useGetGeneratedNotePrototypeQuery,
  useLazyGetGeneratedNotePrototypeQuery,
  useUpdateGeneratedNotePrototypeMutation,
  useRecordNoteGenerationMutation,
  useSaveGeneratedNoteMutation,
  useV2CreateGeneratedNotePrototypeMutation,
  useV2GetGeneratedNotePrototypeQuery,
  useLazyV2GetGeneratedNotePrototypeQuery,
  useV2UpdateGeneratedNotePrototypeMutation,
  useGetGeneratedNoteQuery,
  useLazyGetGeneratedNoteQuery,
  useGetGroupPreferencesQuery,
  useLazyGetGroupPreferencesQuery,
  useUpsertGroupPreferencesMutation,
  useGetGroupPreferencesAsRegardAdminQuery,
  useLazyGetGroupPreferencesAsRegardAdminQuery,
  useUpsertGroupPreferencesAsRegardAdminMutation,
  useGetDefaultNoteSectionsQuery,
  useLazyGetDefaultNoteSectionsQuery,
  useGetSectionTypesQuery,
  useLazyGetSectionTypesQuery,
  useGetUserGeneralPreferencesQuery,
  useLazyGetUserGeneralPreferencesQuery,
  useUpsertUserGeneralPreferencesMutation,
  useGetUserProductStateQuery,
  useLazyGetUserProductStateQuery,
  useUpsertUserProductStateMutation,
  useGetUserProductStatesBulkAsRegardAdminMutation,
  useResetUserOnboardingAsRegardAdminMutation,
  useGetUserPromptPreferencesQuery,
  useLazyGetUserPromptPreferencesQuery,
  useUpsertUserPromptPreferencesMutation,
  useGetAllUserBasePromptsQuery,
  useLazyGetAllUserBasePromptsQuery,
  useTriggerPatientLoadMutation,
  useGetPreOnboardingPreviewQuery,
  useLazyGetPreOnboardingPreviewQuery,
  useTriggerPreOnboardingMutation,
  useGetPreOnboardingStatusQuery,
  useLazyGetPreOnboardingStatusQuery,
  useGetUserPreOnboardingStatusQuery,
  useLazyGetUserPreOnboardingStatusQuery,
  useGetPreOnboardingStatusReportMutation,
  useVerifyPreOnboardingMutation,
  useGetPreOnboardingBatchProgressQuery,
  useLazyGetPreOnboardingBatchProgressQuery,
  useGetPreOnboardingCandidatesReportMutation,
  useGetCandidateNotesQuery,
  useLazyGetCandidateNotesQuery,
  useGetLearnedNotesForNoteTypeQuery,
  useLazyGetLearnedNotesForNoteTypeQuery,
  useLearnFromNoteForNoteTypeMutation,
  useGetChartNotesQuery,
  useLazyGetChartNotesQuery,
  useGetChartNoteContentQuery,
  useLazyGetChartNoteContentQuery,
  useRefreshCacheQuery,
  useLazyRefreshCacheQuery,
} = injectedRtkApi
